import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output
} from '@angular/core';
import type { DealStatusFindInterface } from '@zaoblako/shared/modules/business/dto/deal-status/deal-status-find.dto';
import { DealStatusService } from '@zaoblako/web/core/services/deal/deal-status.service';
import type { FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import { MultiSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/multi-select/multi-select.component';
import { reconcileSelectionByNumericIds } from '@zaoblako/web/shared/helpers/dropdown-catalog-selection-reconcile.helper';
import { DropdownEntityCacheService } from '@zaoblako/web/shared/services/dropdown-entity-cache.service';
import { map } from 'rxjs/operators';

@Component({
  selector: 'crm-deal-status-multi-select',
  standalone: true,
  imports: [CommonModule, MultiSelectComponent],
  templateUrl: './deal-status-multi-select.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DealStatusMultiSelectComponent implements OnInit, OnDestroy {
  private static readonly ENTITY_CACHE_KIND = 'deal-status';

  @Input() public withoutIds: number[] = [];

  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  @Input() public label = '';
  @Input() public statuses: DealStatusFindInterface[] = [];

  @Input() public selectedStatuses: DealStatusFindInterface[] = [];
  @Output() public selectedStatusesChange = new EventEmitter<DealStatusFindInterface[]>();
  @Output() public openedChange = new EventEmitter<boolean>();

  @Input() public customRequest = false;
  @Input() public sort: string;
  @Input() public class: string;
  @Input() public styleClass: string;
  @Input() public showClear = true;
  @Input() public disabled = false;
  @Input() public isError = false;
  @Input() public isFloatLabel = true;
  @Input() public placeholder = '\u00A0';
  @Input() public display = 'chip';
  @Input() public appendTo: string;
  @Input() public itemStyleClass: string;

  private loadedStatuses = false;
  private timer: ReturnType<typeof setInterval>;

  @Input() public set setSelectedStatusIds(ids: number[]) {
    if (ids?.length) {
      ids.forEach((id, index) => {
        if (typeof id === 'string') {
          ids[index] = +id;
        }
      });
    }

    if (
      ids?.length &&
      ((this.selectedStatuses?.length &&
        !this.selectedStatuses?.every((selected) => ids.includes(selected.id))) ||
        !this.selectedStatuses?.length)
    ) {
      if (this.loadedStatuses) {
        this.applySelectionFromRequestedIds(ids);
      } else {
        if (this.timer) {
          clearInterval(this.timer);
        }
        this.timer = setInterval(() => {
          if (this.loadedStatuses) {
            this.applySelectionFromRequestedIds(ids);
            clearInterval(this.timer);
          }
        }, 500);
      }
    }
  }

  public constructor(
    private readonly dealStatusService: DealStatusService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly entityCache: DropdownEntityCacheService
  ) {}

  public onSelectedStatusesChange(statuses: DealStatusFindInterface[]): void {
    this.entityCache.putEntities(DealStatusMultiSelectComponent.ENTITY_CACHE_KIND, statuses ?? []);
    this.selectedStatusesChange.emit(statuses);
  }

  public ngOnInit(): void {
    if (!this.customRequest) {
      const fingerprint = this.entityCache.fingerprint({ excludeIds: this.withoutIds });
      this.entityCache
        .loadFirstPage(DealStatusMultiSelectComponent.ENTITY_CACHE_KIND, fingerprint, () =>
          this.dealStatusService.findWithPagination({
            excludeIds: this.withoutIds
          }).pipe(
            map((page) => {
              const items = page?.items ?? [];
              return { items, count: page?.count ?? items.length };
            })
          )
        )
        .subscribe({
          next: ({ items }) => {
            this.statuses = items;
            this.loadedStatuses = true;
            this.cdRef.markForCheck();
          },
          error: () => {
            this.loadedStatuses = true;
            if (this.timer) {
              clearInterval(this.timer);
            }
          }
        });
    } else {
      this.loadedStatuses = true;
    }
  }

  public ngOnDestroy(): void {
    if (this.timer) {
      clearInterval(this.timer);
    }
  }

  private applySelectionFromRequestedIds(requestedIds: number[]): void {
    const { selected, hasStaleKeys } = reconcileSelectionByNumericIds(this.statuses, requestedIds);
    this.selectedStatuses = selected;
    this.entityCache.putEntities(DealStatusMultiSelectComponent.ENTITY_CACHE_KIND, selected);
    if (hasStaleKeys) {
      this.onSelectedStatusesChange(selected);
    }
    this.cdRef.markForCheck();
  }
}
